import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {ExamModule, UserModule} from '@cp/model';

export interface DeleteModuleWithProgressDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (deleteProgress: boolean) => void;
  module: ExamModule | null;
  userModule: UserModule | null;
  loading?: boolean;
}

export default function DeleteModuleWithProgressDialog({
  open,
  onClose,
  onConfirm,
  module,
  userModule,
  loading = false,
}: DeleteModuleWithProgressDialogProps) {
  const [deleteProgress, setDeleteProgress] = useState(false);

  useEffect(() => {
    if (open) {
      setDeleteProgress(false);
    }
  }, [open]);

  if (!module || !userModule) return null;

  const handleConfirm = () => {
    onConfirm(deleteProgress);
  };

  const handleClose = () => {
    setDeleteProgress(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="delete-module-dialog">
      <DialogTitle data-testid="delete-module-dialog-title">Remove Module</DialogTitle>
      <DialogContent>
        <Box sx={{mb: 2}} data-testid="delete-module-dialog-content">
          <Typography variant="body1" gutterBottom>
            Are you sure you want to remove <strong>{module.name}</strong> from your selected modules?
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This will remove all {(userModule.topics ?? []).length} selected topics for this module.
          </Typography>
        </Box>
        <Box sx={{mt: 3, mb: 2}}>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteProgress}
                onChange={(e) => setDeleteProgress(e.target.checked)}
                disabled={loading}
                data-testid="delete-progress-checkbox"
              />
            }
            label={
              <Box>
                <Typography variant="body2">Also delete all progress for this module</Typography>
                <Typography variant="caption" color="text.secondary">
                  This will permanently delete your quiz scores, study history, and progress data for this module
                </Typography>
              </Box>
            }
          />
        </Box>
        <Alert severity="warning" sx={{mt: 2}} data-testid="delete-module-warning">
          This action cannot be undone. You can always add this module back later if needed.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading} data-testid="cancel-delete-button">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          data-testid="confirm-delete-button"
        >
          Remove Module
        </Button>
      </DialogActions>
    </Dialog>
  );
}
